import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { ThemeContext } from "../../../../../context/ThemeContextProvider";
import { fetchTakeawayOrders } from "../../../../../services/pos/Tables";
import CreateOrderForm from "../../MakeOrder/takeaway/OrderDetails/Openorder/CreateOrderForm";
import TakeawayModalContent from "../../MakeOrder/takeaway/TakeawayModalContent";
import { useSettings } from "../../../../../context/SettingsContext";
import OrderItemsDialog from "./OrderItemsDialog";

const TakeawaySection = () => {
  const { mode } = useContext(ThemeContext);
  const createFormRef = useRef(null);
  const searchInputRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState("الكل");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isAddItemsOpen, setIsAddItemsOpen] = useState(false);
  const { settings, loading, error } = useSettings();

  // NEW: حالة Dialog عرض أصناف الأوردر
  const [orderItemsDialogOpen, setOrderItemsDialogOpen] = useState(false);
  const [selectedOrderIdForItems, setSelectedOrderIdForItems] = useState(null);

  // جديد: تفاصيل الأوردر المحدد للعرض
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  // جديد: تحويل وقت مثل "21:22:33" إلى "9:22 م" (نظام 12 ساعة)
  const formatTime12 = (timeStr) => {
    if (!timeStr) return "-";
    const parts = timeStr.split(":");
    const h = Number(parts[0] ?? 0);
    const m = parts[1] ?? "00";
    const ampm = h >= 12 ? "م" : "ص";
    const hour12 = h % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  useEffect(() => {
  console.log("🧾 نوع ترقيم الفواتير الحالي:", settings?.invoice_incremant);
}, [settings]);

  const loadOrders = async () => {
    try {
      const branchId = localStorage.getItem("branch_id");
      if (!branchId) throw new Error("❌ لا يوجد branch_id في localStorage");

      const data = await fetchTakeawayOrders(branchId);

      // 👇 نقرأ إعدادات الفاتورة
      let orderIncrementKey = "general_increment";
      if (settings?.invoice_incremant === "daily_increment") orderIncrementKey = "daily_increment";
      else if (settings?.invoice_incremant === "monthly_increment") orderIncrementKey = "monthly_increment";

      const enriched = data.map((item) => ({
        id: item.id,
        status: item.status === "pending" ? "في التجهيز" : "مكتمل",
        customer: item.customer?.name || "-",
        pilotName: item.delivery?.name || "-",
        phone: item.customer?.phone1 || "",
        address: item.customer?.address1 || "",
        time: formatTime12(item.open_time),
        total: item.total || 0,
        orderNumber: `#${item[orderIncrementKey] ?? item.general_increment ?? "-"}`,
        timer: 0,
        // NEW: عدد الأصناف في الطلب
        itemsCount: Array.isArray(item.items) ? item.items.length : 0,
        items: (item.items || []).map((it) => ({
          id: it.id,
          product_name: it.product_name,
          quantity: it.quantity,
          price: it.price,
          total: it.total,
        })),
      }));

      setOrders(enriched);
    } catch (err) {
      console.error("❌ فشل تحميل طلبات التيك أواي:", err.message);
    }
  };


useEffect(() => {
  if (settings && !loading) {
    console.log("📦 إعادة تحميل الطلبات بناءً على الإعدادات:", settings.invoice_incremant);
    loadOrders();
  }
}, [settings, loading]);
  // تحديث البيانات كل 10 دقائق
  useEffect(() => {
    const interval = setInterval(() => {
      loadOrders();
    }, 10 * 60 * 1000); // 10 دقائق بالمللي ثانية
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          ["في التجهيز", "قيد التوصيل"].includes(order.status)
            ? { ...order, timer: order.timer + 1 }
            : order
        )
      );
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    localStorage.setItem("current_component", "takeaway");
  }, []);

  // ✅ اختصار F7 للتمرير إلى CreateOrderForm وتركيز الحقل الأول
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F7" || e.keyCode === 118) {
        e.preventDefault();
        if (createFormRef.current) {
          createFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          const first = createFormRef.current.querySelector("input, textarea, select, [tabindex]");
          if (first && typeof first.focus === "function") first.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ✅ اختصار F3 للتركيز على خانة البحث
  useEffect(() => {
    const handleF3 = (e) => {
      if (e.key === "F3" || e.keyCode === 114) {
        e.preventDefault();
        const input = searchInputRef.current;
        if (input) {
          // input قد يكون HTMLElement أو مكوّن MUI internals
          if (typeof input.focus === "function") input.focus();
          if (typeof input.select === "function") input.select();
        }
      }
    };
    window.addEventListener("keydown", handleF3);
    return () => window.removeEventListener("keydown", handleF3);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const filteredOrders = orders.filter((order) => {
    const searchFields = [
      order.customer,
      order.pilotName,
      order.phone,
      order.address,
      order.time,
      order.total.toString(),
      order.orderNumber,
    ].join(" ").toLowerCase();

    return (
      (filterStatus === "الكل" || order.status === filterStatus) &&
      searchFields.includes(searchQuery.toLowerCase())
    );
  });

  // NEW: دالة فتح Dialog عرض الأصناف
  const handleOpenOrderItems = (orderId) => {
    setSelectedOrderIdForItems(orderId);
    setOrderItemsDialogOpen(true);
  };
  
  // NEW: دالة إغلاق Dialog عرض الأصناف
  const handleCloseOrderItems = () => {
    setOrderItemsDialogOpen(false);
    setSelectedOrderIdForItems(null);
  };

  const statusColors = {
    مكتمل: "#4CAF50",
    "في التجهيز": "#FFC107",
  };

  return (
    <Box sx={{ p: 2, bgcolor: mode === "dark" ? "#1F1D2B" : "#fff" }}>
      {/* CreateOrderForm ظاهر دائماً فوق الفلاتر والبحث */}
      {/* أضفنا tabIndex حتى يمكن التركيز عليها عبر الاختصار F7 */}
      <Box sx={{ mb: 2 }} ref={createFormRef} tabIndex={-1} aria-label="create-order-form-wrapper">
        <CreateOrderForm
          onSuccess={(res) => {
            const createdId = res?.id || res?.data?.order_id;
            if (!createdId) return alert("❌ فشل في جلب رقم الطلب الجديد");
            setSelectedOrderId(createdId);
            setIsAddItemsOpen(true);
            loadOrders();
          }}
        />
      </Box>

      {/* فلاتر البحث */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          {["الكل", "مكتمل", "في التجهيز", ].map((status) => (
            <Button
              key={status}
              variant={filterStatus === status ? "contained" : "outlined"}
              onClick={() => setFilterStatus(status)}
              sx={{
                bgcolor: filterStatus === status ? "#F8BBD0" : "transparent",
                color:
                  filterStatus === status ? "#000" : mode ? "#c0c0c0" : "#333",
                borderRadius: "10px",
                px: 3,
                py: 1,
                "&:hover": { bgcolor: "#F48FB1", color: "#fff" },
              }}
            >
              {status}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            placeholder="البحث عن الطلبات..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            sx={{ width: 250 }}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")}>
                    {" "}
                    <ClearIcon />{" "}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={() => {
              if (createFormRef.current) {
                createFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                const first = createFormRef.current.querySelector("input, textarea, select, [tabindex]");
                if (first && typeof first.focus === "function") first.focus();
              }
            }}
            title="الانتقال لنموذج إنشاء طلب (F7)"
          >
            إنشاء طلب جديد
          </Button>
        </Box>
      </Box>

      {/* قائمة الطلبات */}
      <Grid container spacing={2}>
        {filteredOrders.map((order, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
            <Paper
              sx={{
                background: mode === "dark" 
                  ? "linear-gradient(135deg, #2a2838 0%, #232334 100%)"
                  : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                color: mode === "dark" ? "#fff" : "#232334",
                borderRadius: 4,
                boxShadow: mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.4)"
                  : "0 4px 20px rgba(0,0,0,0.08)",
                p: 0,
                overflow: "hidden",
                minHeight: { xs: 180, sm: 195, md: 210 },
                position: "relative",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                border: mode === "dark" ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.06)",
                "&:hover": {
                  boxShadow: mode === "dark"
                    ? "0 12px 40px rgba(234,124,105,0.35)"
                    : "0 8px 32px rgba(234,124,105,0.2)",
                  transform: "translateY(-4px) scale(1.02)",
                  borderColor: "#EA7C69",
                },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
              onClick={() => {
                setSelectedOrderId(order.id);
                setIsAddItemsOpen(true);
              }}
            >
              {/* شريط علوي ملون حسب الحالة */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: order.status === "مكتمل"
                    ? "linear-gradient(90deg, #4CAF50 0%, #66BB6A 100%)"
                    : "linear-gradient(90deg, #FFC107 0%, #FFD54F 100%)",
                  zIndex: 3,
                }}
              />

              {/* Header: Order Number & Status */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: { xs: 1.2, sm: 1.5 },
                  pb: { xs: 0.8, sm: 1 },
                  position: "relative",
                  gap: { xs: 1, sm: 1.5, md: 2 },
                  flexWrap: "wrap",
                  background: mode === "dark"
                    ? "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 100%)"
                    : "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, transparent 100%)",
                }}
              >
                {/* NEW: أيقونة عدد الأصناف - في البداية */}
                {order.itemsCount > 0 && (
                  <Box
                    sx={{
                      zIndex: 10,
                      cursor: "pointer",
                      order: 1,
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.15)",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenOrderItems(order.id);
                    }}
                  >
                    <Badge
                      badgeContent={order.itemsCount}
                      color="error"
                      max={99}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: { xs: "8px", sm: "9px" },
                          fontWeight: 700,
                          minWidth: { xs: "14px", sm: "16px" },
                          height: { xs: "14px", sm: "16px" },
                          padding: "0 3px",
                          boxShadow: "0 2px 8px rgba(244,67,54,0.4)",
                          animation: "pulse 2s infinite",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: mode === "dark" ? "#1a1a2e" : "#fff",
                          borderRadius: "50%",
                          width: { xs: 28, sm: 32 },
                          height: { xs: 28, sm: 32 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: mode === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.5)"
                            : "0 2px 8px rgba(0,0,0,0.15)",
                          border: `2px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "scale(1.1)",
                            boxShadow: mode === "dark"
                              ? "0 6px 16px rgba(234,124,105,0.4)"
                              : "0 4px 12px rgba(234,124,105,0.3)",
                            borderColor: "#EA7C69",
                          },
                          "&:active": {
                            transform: "scale(0.95)",
                          },
                          flexShrink: 0,
                        }}
                      >
                        <RestaurantMenuIcon
                          sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: mode === "dark" ? "#EA7C69" : "#666",
                          }}
                        />
                      </Box>
                    </Badge>
                  </Box>
                )}

                {/* المحتوى الموجود - الحالة ورقم الطلب */}
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: { xs: 1, sm: 1.5, md: 2 }, 
                  flex: 1, 
                  justifyContent: "flex-end",
                  order: 2,
                  minWidth: 0,
                }}>
                  {/* الحالة بشكل شارة دائرية مع نص */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 0.8, md: 1 },
                      ml: { xs: 0.5, sm: 1, md: 2 },
                      px: { xs: 1, sm: 1.5 },
                      py: { xs: 0.4, sm: 0.5 },
                      bgcolor:
                        order.status === "مكتمل"
                          ? "#4CAF50"
                          : order.status === "في التجهيز"
                          ? "#FFC107"
                          : "#fff",
                      color:
                        order.status === "مكتمل"
                          ? "#fff"
                          : order.status === "في التجهيز"
                          ? "#232334"
                          : "#232334",
                      borderRadius: { xs: "16px", sm: "20px" },
                      fontWeight: 700,
                      fontSize: { xs: 12, sm: 14, md: 15 },
                      minWidth: { xs: 75, sm: 90 },
                      justifyContent: "center",
                      boxShadow: order.status === "مكتمل"
                        ? "0 4px 12px rgba(76,175,80,0.3)"
                        : "0 4px 12px rgba(255,193,7,0.3)",
                      flexShrink: 0,
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 10, sm: 12 },
                        height: { xs: 10, sm: 12 },
                        borderRadius: "50%",
                        bgcolor:
                          order.status === "مكتمل"
                            ? "#fff"
                            : order.status === "في التجهيز"
                            ? "#fff"
                            : "#fff",
                        border: `2px solid ${
                          order.status === "مكتمل"
                            ? "#fff"
                            : order.status === "في التجهيز"
                            ? "#FFC107"
                            : "#fff"
                        }`,
                        mr: { xs: 0.5, sm: 0.8, md: 1 },
                        flexShrink: 0,
                      }}
                    />
                    <Typography 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: 12, sm: 14, md: 15 },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {order.status === "مكتمل" ? "تم الانتهاء" : order.status}
                    </Typography>
                  </Box>

                  {/* رقم الطلب */}
                  <Box
                    sx={{
                      bgcolor: "#1ecb6b",
                      color: "#fff",
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.4, sm: 0.5 },
                      borderRadius: { xs: "10px", sm: "12px" },
                      fontWeight: 700,
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      boxShadow: "0 4px 12px rgba(30,203,107,0.35)",
                      display: "flex",
                      alignItems: "center",
                      minWidth: { xs: 50, sm: 60 },
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 6px 16px rgba(30,203,107,0.45)",
                      },
                    }}
                  >
                    {order.orderNumber}
                  </Box>

                  {/* أيقونة العرض */}
                  <Tooltip title="عرض الأصناف" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrderId(order.id);
                        setSelectedOrderDetails(orders.find((o) => o.id === order.id) || order);
                        setIsViewOpen(true);
                      }}
                      sx={{
                        ml: { xs: 0, sm: 0.5, md: 1 },
                        color: mode === "dark" ? "#fff" : "#666",
                        bgcolor: mode === "dark" 
                          ? "rgba(255,255,255,0.08)" 
                          : "rgba(0,0,0,0.04)",
                        p: { xs: 0.5, sm: 0.75 },
                        "&:hover": { 
                          bgcolor: mode === "dark"
                            ? "rgba(234,124,105,0.2)"
                            : "rgba(234,124,105,0.1)",
                          color: "#EA7C69",
                          transform: "scale(1.1)",
                        },
                        flexShrink: 0,
                        transition: "all 0.2s",
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    </IconButton>
                  </Tooltip>

                  {/* رقم تسلسلي */}
                  <Box
                    sx={{
                      bgcolor: "#F8BBD0",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: { xs: 16, sm: 20, md: 22 },
                      px: { xs: 1.8, sm: 2.2 },
                      py: { xs: 0.4, sm: 0.5 },
                      borderRadius: { xs: "0 0 14px 14px", sm: "0 0 18px 18px" },
                      minWidth: { xs: 32, sm: 38 },
                      textAlign: "center",
                      position: "absolute",
                      top: 0,
                      right: 0,
                      zIndex: 2,
                      boxShadow: "0 4px 12px rgba(248,187,208,0.4)",
                      transition: "transform 0.2s",
                    }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </Box>
                </Box>
              </Box>

              {/* قسم معلومات إضافية */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: { xs: 1, sm: 1.5, md: 2 },
                  py: { xs: 0.5, sm: 0.8 },
                  borderTop: mode === "dark"
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "1px solid rgba(0,0,0,0.06)",
                  borderBottom: mode === "dark"
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "1px solid rgba(0,0,0,0.06)",
                }}
              >
                {/* Time */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: { xs: 6, sm: 8 },
                      height: { xs: 6, sm: 8 },
                      borderRadius: "50%",
                      bgcolor: "#EA7C69",
                      animation: "pulse 2s infinite",
                    }}
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: mode === "dark" ? "#b0bec5" : "#666",
                      fontSize: { xs: "11px", sm: "12px", md: "13px" },
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                    }}
                  >
                    {order.time}
                  </Typography>
                </Box>

                {/* عدد الأصناف */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <RestaurantMenuIcon 
                    sx={{ 
                      fontSize: { xs: 12, sm: 14 },
                      color: mode === "dark" ? "#EA7C69" : "#666",
                    }} 
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: mode === "dark" ? "#b0bec5" : "#666",
                      fontSize: { xs: "11px", sm: "12px", md: "13px" },
                      fontWeight: 600,
                    }}
                  >
                    {order.itemsCount} صنف
                  </Typography>
                </Box>
              </Box>

              {/* Main Content - اسم العميل */}
              <Box
                sx={{
                  px: { xs: 1.2, sm: 1.5, md: 2 },
                  py: { xs: 1.2, sm: 1.5, md: 2 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.8,
                  alignItems: "flex-end",
                  flex: 1,
                  justifyContent: "center",
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: mode === "dark" ? "#888" : "#999",
                    fontSize: { xs: "10px", sm: "11px" },
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  اسم العميل
                </Typography>
                <Typography
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: 16, sm: 18, md: 20 }, 
                    color: mode === "dark" ? "#fff" : "#232334",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                    letterSpacing: "0.5px",
                    textShadow: mode === "dark" 
                      ? "0 2px 4px rgba(0,0,0,0.3)" 
                      : "none",
                  }}
                >
                  {order.customer}
                </Typography>
              </Box>

              {/* Footer: Total */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #F8BBD0 0%, #F48FB1 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: 17, sm: 19, md: 22 },
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 0.6, sm: 0.8 },
                  borderRadius: { xs: "14px", sm: "16px" },
                  position: "absolute",
                  bottom: { xs: 8, sm: 12 },
                  right: { xs: 8, sm: 12 },
                  boxShadow: "0 6px 20px rgba(248,187,208,0.5)",
                  minWidth: { xs: 60, sm: 70 },
                  textAlign: "center",
                  letterSpacing: "1.5px",
                  border: "2px solid rgba(255,255,255,0.3)",
                  transition: "all 0.2s",
                  backdropFilter: "blur(4px)",
                }}
              >
                ${order.total}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* نافذة عرض الأصناف (عرض فقط) */}
      {isViewOpen && selectedOrderDetails && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: 1400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
          onClick={() => setIsViewOpen(false)}
        >
          <Box
            sx={{
              bgcolor: mode === "dark" ? "#232334" : "#fff",
              color: mode === "dark" ? "#fff" : "#232334",
              borderRadius: 2,
              boxShadow: 8,
              p: 2,
              width: "min(900px, 98vw)",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              onClick={() => setIsViewOpen(false)}
              sx={{ position: "absolute", left: 8, top: 8, color: "#EA7C69" }}
            >
              <ClearIcon />
            </IconButton>

            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              أصناف الطلب - {selectedOrderDetails.orderNumber || `#${selectedOrderDetails.id}`}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              العميل: {selectedOrderDetails.customer} — الحالة: {selectedOrderDetails.status}
            </Typography>

            {/* جدول مبسّط للأصناف */}
            <Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 80px 100px 100px", gap: 1, mb: 1, fontWeight: 800 }}>
                <Typography>الاسم</Typography>
                <Typography>الكمية</Typography>
                <Typography>السعر</Typography>
                <Typography>المجموع</Typography>
              </Box>

              {(selectedOrderDetails.items || []).length === 0 ? (
                <Typography sx={{ color: "#b0bec5" }}>لا توجد أصناف في هذا الطلب.</Typography>
              ) : (
                (selectedOrderDetails.items || []).map((it) => (
                  <Box key={it.id} sx={{ display: "grid", gridTemplateColumns: "1fr 80px 100px 100px", gap: 1, py: 0.5, borderBottom: "1px dashed rgba(0,0,0,0.06)" }}>
                    <Typography sx={{ fontWeight: 700 }}>{it.product_name}</Typography>
                    <Typography>{it.quantity}</Typography>
                    <Typography>{it.price}</Typography>
                    <Typography>{it.total}</Typography>
                  </Box>
                ))
              )}
            </Box>

            {/* زر إعادة التحميل / إغلاق */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setIsViewOpen(false);
                  loadOrders();
                }}
              >
                إغلاق وتحديث
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Dialog TableModalDialog (الموجود سابقًا) */}
      <TakeawayModalContent
        open={isAddItemsOpen}
        onClose={() => setIsAddItemsOpen(false)}
        type="addItem"
        orderId={selectedOrderId}
        tableId={null}
        rootId={localStorage.getItem("branch_id")}
        onSuccess={() => {
          setIsAddItemsOpen(false);
          loadOrders();
        }}
      />

      {/* NEW: Dialog عرض أصناف الأوردر */}
      <OrderItemsDialog
        open={orderItemsDialogOpen}
        onClose={handleCloseOrderItems}
        orderId={selectedOrderIdForItems}
      />

      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.6;
              transform: scale(0.9);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default TakeawaySection;
